/**
 * Button Component
 *
 * A flexible button component with multiple variants, sizes, and states.
 *
 * @example
 * <Button variant="primary" size="md" onClick={handleClick}>
 *   Click me
 * </Button>
 */

import React, { forwardRef } from 'react';
import { Icon } from '@iconify/react';
import Spinner from '../Spinner/Spinner';
import './Button.css';

const Button = forwardRef(({
  children,
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  disabled = false,
  loading = false,
  leftIcon,
  rightIcon,
  iconOnly = false,
  type = 'button',
  className = '',
  ...props
}, ref) => {
  const isDisabled = disabled || loading;

  const classNames = [
    'ds-btn',
    `ds-btn--${variant}`,
    `ds-btn--${size}`,
    fullWidth && 'ds-btn--full-width',
    iconOnly && 'ds-btn--icon-only',
    loading && 'ds-btn--loading',
    className,
  ].filter(Boolean).join(' ');

  return (
    <button
      ref={ref}
      type={type}
      className={classNames}
      disabled={isDisabled}
      {...props}
    >
      {loading && (
        <span className="ds-btn__spinner">
          <Spinner size="sm" color="currentColor" />
        </span>
      )}

      {!loading && leftIcon && (
        <span className="ds-btn__icon ds-btn__icon--left">
          {typeof leftIcon === 'string' ? (
            <Icon icon={leftIcon} />
          ) : (
            leftIcon
          )}
        </span>
      )}

      {children && (
        <span className="ds-btn__content">{children}</span>
      )}

      {!loading && rightIcon && (
        <span className="ds-btn__icon ds-btn__icon--right">
          {typeof rightIcon === 'string' ? (
            <Icon icon={rightIcon} />
          ) : (
            rightIcon
          )}
        </span>
      )}
    </button>
  );
});

Button.displayName = 'Button';

export default Button;
